import { z } from 'zod';
import { toIso } from './util';
import defaults from './quote-defaults.json';

/**
 * Canonical OS quote defaults — the SINGLE source of truth lives in
 * ./quote-defaults.json. It is consumed both here (the @mfx/contracts type +
 * factory) and by the OS build's codegen (scripts/gen-quote-defaults.js, which
 * emits public/js/quote-defaults.generated.js), so the OS no longer keeps its
 * own physical copy in core.js. quote.test.ts holds an independent transcription
 * of the OS default quote and asserts newDraft() deep-equals it, so the JSON can
 * never silently drift from the known-good OS shape.
 */
export const FDEF = defaults.FDEF;
export const DEFAULT_TERMS: readonly string[] = defaults.DEFAULT_TERMS;
export const QDEF: readonly number[] = defaults.QDEF;

/**
 * Canonical quote lifecycle statuses — the SINGLE source of truth, mirroring
 * STATE_MACHINES.quotes in the OS functions (functions/index.js). Import this
 * everywhere a quote status is validated, filtered, or styled so the OS
 * backend, the CRM pages, and this contract can never drift apart again.
 *
 *   draft → approval → ready → sent → won | lost
 *   (rejected out of approval; archived is the terminal parking state)
 */
export const QUOTE_STATUSES = [
  'draft',
  'approval',
  'ready',
  'sent',
  'won',
  'lost',
  'rejected',
  'archived',
] as const;
export const QuoteStatusSchema = z.enum(QUOTE_STATUSES);
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

/** Narrowing guard for untrusted status strings. */
export function isQuoteStatus(v: unknown): v is QuoteStatus {
  return typeof v === 'string' && (QUOTE_STATUSES as readonly string[]).includes(v);
}

/** Human label + a stable style token for each canonical status (UI helper). */
export const QUOTE_STATUS_META: Record<QuoteStatus, { label: string; tone: string }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  approval: { label: 'Pending approval', tone: 'warning' },
  ready: { label: 'Ready to send', tone: 'info' },
  sent: { label: 'Sent', tone: 'info' },
  won: { label: 'Won', tone: 'success' },
  lost: { label: 'Lost', tone: 'danger' },
  rejected: { label: 'Rejected', tone: 'danger' },
  archived: { label: 'Archived', tone: 'muted' },
};

/** A priced quantity tier. Default-draft `qtys` is a plain number[] (`QDEF`). */
export const QtyTierSchema = z.object({
  qty: z.number(),
  ppu: z.number(),
  setup: z.number(),
  total: z.number(),
});
export type QtyTier = z.infer<typeof QtyTierSchema>;

/** The pricing/spec field bag. FDEF is the default; extra keys are tolerated. */
export const QuoteFieldsSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean()]),
);
export type QuoteFields = typeof FDEF & { quoteDate?: string } & Record<string, string | number | boolean>;

export const QuoteSchema = z
  .object({
    id: z.string(),
    quoteNum: z.string(),
    rev: z.string(),
    // Canonical values live in QUOTE_STATUSES / QuoteStatusSchema. Kept as a
    // permissive string on READ so legacy or in-flight CRM-pipeline values don't
    // throw during parse; validate against QuoteStatusSchema on WRITE.
    status: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    sentAt: z.string().nullable(),
    closedAt: z.string().nullable(),
    wonAmount: z.number().nullable(),
    lostReason: z.string(),
    customerId: z.string().nullable(),
    customerName: z.string().optional(),
    fields: QuoteFieldsSchema,
    qtys: z.array(z.union([z.number(), QtyTierSchema])),
    terms: z.array(z.string()),
    createdBy: z.string().optional(),
    createdById: z.string().optional(),
    source: z.string().optional(),
  })
  .passthrough();

export type Quote = z.infer<typeof QuoteSchema>;

export interface NewDraftOptions {
  /** Document id (OS: uid()). Required so output is deterministic/testable. */
  id: string;
  /** Quote number (OS: genQN() / server sequence). Required for determinism. */
  quoteNum: string;
  /** Timestamp for createdAt/updatedAt/quoteDate. Defaults to now. */
  now?: Date | string;
  /** fields.estimator (OS: getUserName()). */
  estimator?: string;
  /** Top-level createdBy (OS: getUserName()). Defaults to estimator. */
  createdBy?: string;
  /** Top-level createdById (OS: getUserId()). */
  createdById?: string;
  customerId?: string | null;
  customerName?: string;
}

/**
 * Build a fresh draft quote — the canonical OS `newQuote()` baseline object.
 * Volatile fields are injected via opts so the result is deterministic.
 */
export function newDraft(opts: NewDraftOptions): Quote {
  const ts = toIso(opts.now);
  const estimator = opts.estimator ?? '';
  const quote: Quote = {
    id: opts.id,
    quoteNum: opts.quoteNum,
    rev: 'A',
    status: 'draft',
    createdAt: ts,
    updatedAt: ts,
    sentAt: null,
    closedAt: null,
    wonAmount: null,
    lostReason: '',
    customerId: opts.customerId ?? null,
    fields: { ...FDEF, quoteDate: ts.split('T')[0], estimator },
    qtys: [...QDEF],
    terms: [...DEFAULT_TERMS],
    createdBy: opts.createdBy ?? estimator,
    createdById: opts.createdById ?? '',
  };
  if (opts.customerName !== undefined) quote.customerName = opts.customerName;
  return quote;
}
